var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a, _b;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import * as ReactDOM from "react-dom/client";
import { getFontAwesomeIcon, hasValue, isFontawesomeIcon, roundOff, toBool, toNum, toStr } from '@utils';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { extractValueAndFormatters } from './pill-input-utils';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import classNames from 'classnames';
import FormField from '@widget/forms/inputs/FormField';
import Label from '@widget/forms/inputs/Label';
import Input from '@widget/forms/inputs/Input';
import ToggleFilter from '@widget/forms/inputs/ToggleFilter';
import { Dropdown } from '@widget/popups/Dropdown';
import ButtonComponent from '@widget/buttons/ButtonComponent';
// Runtime accessor — codemirror-bundle loads before UXP so this is always defined
function getCM() {
    return window.UXPCodeMirror;
}
// WidgetType base resolved at module-eval time (safe: codemirror-bundle is synchronous & prior)
const _WidgetBase = ((_b = (_a = window.UXPCodeMirror) === null || _a === void 0 ? void 0 : _a.WidgetType) !== null && _b !== void 0 ? _b : class {
});
/**
 * @export
 * Available format options for pills
 */
export const FormatOptions = [
    {
        id: 'string',
        label: 'Text',
        dispalyLabel: 'ABC',
        formatValue: (val) => toStr(val)
    },
    {
        id: 'number',
        label: 'Number',
        dispalyLabel: '123',
        formatValue: (val, configs, placeholder) => {
            const num = toNum(val);
            if (!hasValue(num, true, true))
                return placeholder || val;
            if (!!configs && hasValue(configs === null || configs === void 0 ? void 0 : configs[0], true))
                return roundOff(num, toNum(configs === null || configs === void 0 ? void 0 : configs[0], 0));
            return num;
        },
        renderConfigs: (configs, onChange) => {
            return _jsx(_Fragment, { children: _jsxs(FormField, { children: [_jsx(Label, { children: "Decimal Places" }), _jsx(Input, { type: 'number', value: (configs === null || configs === void 0 ? void 0 : configs[0]) || '0', onChange: v => {
                                const parsed = toNum(v, undefined);
                                if (parsed == undefined)
                                    onChange(['0']);
                                if (parsed > 10)
                                    onChange(['10']);
                                if (parsed < 0)
                                    onChange(['0']);
                                else
                                    onChange([parsed.toString()]);
                            }, inputAttr: {
                                step: '1',
                                min: '0',
                                max: '10'
                            } })] }) });
        }
    },
    {
        id: 'boolean',
        label: 'True/False',
        dispalyLabel: 'T/F',
        formatValue: (val) => toBool(val)
    }
];
// Formatter component
const Formatter = ({ formatters, onChange, onClick, disabled, readOnly }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [tempFormatters, setTempFormatters] = useState(formatters);
    const triggerRef = useRef(null);
    // Reset temp state when panel opens
    useEffect(() => {
        if (isPanelOpen) {
            setTempFormatters([...formatters]);
        }
    }, [isPanelOpen, formatters]);
    const formatterId = (!!tempFormatters && hasValue(tempFormatters === null || tempFormatters === void 0 ? void 0 : tempFormatters[0])) ? tempFormatters[0] : null;
    const formatOption = formatterId ? FormatOptions.find(f => f.id === formatterId) : null;
    const handleFormatChange = (newFormat) => {
        setTempFormatters([newFormat]);
    };
    const handleConfigChange = (configs) => {
        setTempFormatters([tempFormatters[0], ...configs]);
    };
    const handleDone = () => {
        onChange([...tempFormatters]);
        setIsPanelOpen(false);
    };
    const handleCancel = () => {
        setTempFormatters([...formatters]);
        setIsPanelOpen(false);
    };
    const toggleOptions = FormatOptions.map(option => ({
        label: option.dispalyLabel,
        value: option.id
    }));
    const renderPreviewContent = () => {
        if (formatOption) {
            return [formatOption.dispalyLabel, ...(tempFormatters || []).slice(1)].join(' | ');
        }
        return _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'magic'] });
    };
    const renderDropdownContent = () => {
        var _a;
        return (_jsxs("div", { className: "uxpcore_pillinput__formatter-content", children: [_jsxs("div", { className: "uxpcore_pillinput__formatter-section", children: [_jsxs(FormField, { children: [_jsx(Label, { children: "Format Type" }), _jsx(ToggleFilter, { value: formatterId || '', options: toggleOptions, onChange: handleFormatChange, className: "uxpcore_pillinput__formatter-toggle" })] }), (_a = formatOption === null || formatOption === void 0 ? void 0 : formatOption.renderConfigs) === null || _a === void 0 ? void 0 : _a.call(formatOption, (tempFormatters || []).slice(1), handleConfigChange)] }), _jsxs("div", { className: "uxpcore_pillinput__formatter-actions", children: [_jsx(ButtonComponent, { title: 'Cancel', onClick: handleCancel, size: 'small', variant: 'secondary' }), _jsx(ButtonComponent, { title: 'Done', onClick: handleDone, size: 'small' })] })] }));
    };
    return (_jsxs("div", { className: "uxpcore_pillinput__formatter", children: [_jsx("div", { className: "uxpcore_pillinput__formatter-preview", onClick: (e) => {
                    if (disabled || readOnly)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    setIsPanelOpen(true);
                    onClick === null || onClick === void 0 ? void 0 : onClick();
                }, onMouseDown: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }, ref: triggerRef, children: renderPreviewContent() }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: isPanelOpen, onToggle: () => setIsPanelOpen(false), minWidth: 100, maxWidth: 200 })] }));
};
/**
 * @export
 * Individual Pill component that renders a formatted value with icon and styling
 */
export const Pill = ({ value, allFields, pillConfiguration, draggable = true, className, onClick, pillValuesSplitFn, typeIndex, showFomatters = false, onChangeFormatters, onClickFormatters, disabled, readOnly }) => {
    var _a;
    const defaultConfiguration = {
        valueIndex: 0,
        renderValue: (value, allFields) => {
            const v = (allFields || []).find(f => f.value == value);
            if (!!v && hasValue(v === null || v === void 0 ? void 0 : v.label))
                return v.label;
            return value.replace(/^\$\./, '');
        },
        icon: 'fas tag',
        backgroundColor: '#424242',
        textColor: '#fff'
    };
    const { content, formatters } = extractValueAndFormatters(value);
    if (!hasValue(content))
        return null;
    const parts = pillValuesSplitFn ? pillValuesSplitFn(content) : content.split(".");
    if (!parts.length)
        return null;
    const indexValue = hasValue(typeIndex, true) ? typeIndex : 1;
    const type = (parts === null || parts === void 0 ? void 0 : parts[indexValue]) || null;
    const config = hasValue(type) && pillConfiguration && (pillConfiguration === null || pillConfiguration === void 0 ? void 0 : pillConfiguration[type])
        ? pillConfiguration[type]
        : (_a = pillConfiguration === null || pillConfiguration === void 0 ? void 0 : pillConfiguration.config) !== null && _a !== void 0 ? _a : defaultConfiguration;
    const { valueIndex, icon, backgroundColor, textColor, renderValue } = config;
    const pillValue = renderValue ? renderValue(content, allFields) : parts[valueIndex] || content;
    const pillBG = typeof backgroundColor === 'string' ? backgroundColor : backgroundColor(content);
    const pillIcon = typeof icon === 'string' ? icon : icon(content);
    const pillColor = typeof textColor === 'string' ? textColor : textColor(content);
    const renderIcon = () => {
        if (isFontawesomeIcon(pillIcon.trim())) {
            return _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(pillIcon === null || pillIcon === void 0 ? void 0 : pillIcon.trim()) });
        }
        if (typeof pillIcon === 'string' && pillIcon) {
            return _jsx("div", { className: 'uxpcore_pillinput__pill-icon-url', style: { backgroundImage: `url(${pillIcon})` } });
        }
        return null;
    };
    const styles = {
        backgroundColor: pillBG,
        borderColor: pillBG,
        color: pillColor
    };
    const onDragStart = (e) => {
        e.dataTransfer.setData('text/plain', '{' + value + '}');
    };
    return (_jsxs("div", { className: classNames('uxpcore_pillinput__pill', className), style: styles, draggable: draggable, onDragStart: onDragStart, onClick: onClick, children: [_jsx("div", { className: 'uxpcore_pillinput__pill-icon', children: renderIcon() }), _jsxs("div", { className: "uxpcore_pillinput__pill-text", children: [pillValue, showFomatters
                        ? _jsx(Formatter, { formatters: formatters, onChange: (updatedFormatters) => {
                                onChangeFormatters([content, ...updatedFormatters].join('|'));
                            }, onClick: onClickFormatters, disabled: disabled, readOnly: readOnly })
                        : null] })] }));
};
// Widget class for CodeMirror 6 pill widgets
class PillWidget extends _WidgetBase {
    constructor(value, allFields, position, pillConfiguration, pillValuesSplitFn, onChangeFormatters, onClickFormatters, onPillClick, disabled, readOnly, showFormatters = true) {
        super();
        this.value = value;
        this.allFields = allFields;
        this.position = position;
        this.pillConfiguration = pillConfiguration;
        this.pillValuesSplitFn = pillValuesSplitFn;
        this.onChangeFormatters = onChangeFormatters;
        this.onClickFormatters = onClickFormatters;
        this.onPillClick = onPillClick;
        this.disabled = disabled;
        this.readOnly = readOnly;
        this.showFormatters = showFormatters;
        this.handleClick = (e) => {
            const mouseEvent = e;
            const target = mouseEvent.target;
            const isFormatterClick = target.closest('.uxpcore_pillinput__formatter-preview');
            if (!isFormatterClick) {
                mouseEvent.preventDefault();
                mouseEvent.stopPropagation();
                setTimeout(() => {
                    var _a;
                    (_a = this.onPillClick) === null || _a === void 0 ? void 0 : _a.call(this, this.value, this.position);
                }, 0);
            }
        };
    }
    toDOM() {
        const container = document.createElement('div');
        container.className = 'uxpcore_pillinput__pill-container';
        container.dataset.value = this.value;
        container.dataset.from = this.position.from.toString();
        container.dataset.to = this.position.to.toString();
        container.addEventListener('mousedown', this.handleClick);
        const root = ReactDOM.createRoot(container);
        const pillComponent = (_jsx(Pill, { value: this.value, pillConfiguration: this.pillConfiguration, pillValuesSplitFn: this.pillValuesSplitFn, allFields: this.allFields, expressionMatcher: /{(.*?)}/, showFomatters: this.showFormatters, onChangeFormatters: (newValue) => {
                var _a;
                (_a = this.onChangeFormatters) === null || _a === void 0 ? void 0 : _a.call(this, this.value, newValue, this.position);
            }, onClickFormatters: this.onClickFormatters, disabled: this.disabled, readOnly: this.readOnly }));
        root.render(pillComponent);
        container.__react_root = root;
        return container;
    }
    destroy(dom) {
        dom.removeEventListener('mousedown', this.handleClick);
        const root = dom.__react_root;
        if (root) {
            setTimeout(() => {
                root.unmount();
                delete dom.__react_root;
            }, 0);
        }
    }
    eq(other) {
        return this.value === other.value;
    }
}
// CodeMirror 6 extension for pills
const createPillExtension = (allFields, pillConfiguration, pillValuesSplitFn, onChangeFormatters, onClickFormatters, onPillClick, expressionMatcher = /{(.*?)}/, disabled, readOnly, showFormatters = true) => {
    const { StateField, EditorView, Decoration, keymap } = getCM();
    const findPillsInDoc = (doc) => {
        const decorations = [];
        const fullText = doc.toString();
        let match;
        const regex = new RegExp(expressionMatcher.source, 'g');
        while ((match = regex.exec(fullText)) !== null) {
            const from = match.index;
            const to = from + match[0].length;
            const value = match[1];
            const position = { from, to };
            const widget = new PillWidget(value, allFields, position, pillConfiguration, pillValuesSplitFn, onChangeFormatters, onClickFormatters, onPillClick, disabled, readOnly, showFormatters);
            decorations.push(Decoration.replace({
                widget,
                block: false,
                side: 1
            }).range(from, to));
        }
        return Decoration.set(decorations);
    };
    const pillField = StateField.define({
        create(state) {
            return findPillsInDoc(state.doc);
        },
        update(decorations, tr) {
            if (!tr.docChanged) {
                return decorations.map(tr.changes);
            }
            return findPillsInDoc(tr.state.doc);
        },
        provide: (f) => EditorView.decorations.from(f)
    });
    const pillKeymap = keymap.of([
        {
            key: "Backspace",
            run: (view) => {
                const { state } = view;
                const { main } = state.selection;
                const decorations = state.field(pillField);
                let pillToDelete = null;
                decorations.between(0, state.doc.length, (from, to, decoration) => {
                    if (decoration.spec.widget instanceof PillWidget) {
                        if ((main.head === to || (main.head > from && main.head <= to)) && main.empty) {
                            pillToDelete = { from, to };
                            return false;
                        }
                    }
                });
                if (pillToDelete) {
                    view.dispatch({
                        changes: { from: pillToDelete.from, to: pillToDelete.to, insert: "" },
                        selection: { anchor: pillToDelete.from }
                    });
                    return true;
                }
                return false;
            }
        },
        {
            key: "Delete",
            run: (view) => {
                const { state } = view;
                const { main } = state.selection;
                const decorations = state.field(pillField);
                let pillToDelete = null;
                decorations.between(0, state.doc.length, (from, to, decoration) => {
                    if (decoration.spec.widget instanceof PillWidget) {
                        if ((main.head === from || (main.head >= from && main.head < to)) && main.empty) {
                            pillToDelete = { from, to };
                            return false;
                        }
                    }
                });
                if (pillToDelete) {
                    view.dispatch({
                        changes: { from: pillToDelete.from, to: pillToDelete.to, insert: "" },
                        selection: { anchor: pillToDelete.from }
                    });
                    return true;
                }
                return false;
            }
        }
    ]);
    return { extension: [pillField, pillKeymap], pillField };
};
/**
 * @export
 * PillInput component - a CodeMirror-based input that converts expressions to visual pills.
 * Supports drag/drop, formatting, and contextual value selection.
 * Uses CodeMirror 6 with React 18 compatibility.
 *
 * @example
 * Basic usage with field options
 * ```tsx
 * <PillInput
 *   value="Hello {user.name}!"
 *   onChange={setValue}
 *   contextDataSections={[
 *     { label: "User", fields: [{ label: "Name", value: "user.name" }] }
 *   ]}
 * />
 * ```
 */
const PillInput = forwardRef(({ value, onChange, placeholder, contextDataSections, pillConfiguration, className, onFocus, onBlur, panelTitle = "Available values", expressionMatcher = /{(.*?)}/, pillValuesSplitFn, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, onClear, showFormatters = true, inspect = false, }, ref) => {
    var _a;
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const editorViewRef = useRef(null);
    const inputContainerRef = useRef(null);
    const hasFields = (!!contextDataSections && contextDataSections.length > 0 && contextDataSections.reduce((t, s) => { var _a; return (t + ((_a = s === null || s === void 0 ? void 0 : s.fields) === null || _a === void 0 ? void 0 : _a.length)); }, 0) > 0);
    const allFields = (contextDataSections || []).reduce((final, s) => { final.push(...(s === null || s === void 0 ? void 0 : s.fields) || []); return final; }, []);
    useImperativeHandle(ref, () => ({
        focus: focusInput,
        insertAtCursor: insertAtCursor
    }));
    const focusInput = () => {
        var _a;
        (_a = editorViewRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const insertAtCursor = (insertValue) => {
        const view = editorViewRef.current;
        if (view) {
            const selection = view.state.selection.main;
            if (!selection.empty) {
                const newPos = selection.from + insertValue.length;
                view.dispatch({
                    changes: { from: selection.from, to: selection.to, insert: insertValue },
                    selection: { anchor: newPos, head: newPos }
                });
            }
            else {
                const pos = selection.head;
                const newPos = pos + insertValue.length;
                view.dispatch({
                    changes: { from: pos, insert: insertValue },
                    selection: { anchor: newPos, head: newPos }
                });
            }
            requestAnimationFrame(() => {
                view.focus();
            });
        }
    };
    const openPanel = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsPanelOpen(true);
    });
    const handleFocus = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsFocused(true);
        if (!hasFields || disabled || readOnly)
            return;
        yield openPanel();
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(new Event('focus'));
    });
    const handleBlur = () => {
        setIsFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const closePanel = () => {
        setIsPanelOpen(false);
    };
    const pillExtensionData = React.useMemo(() => createPillExtension(allFields, pillConfiguration, pillValuesSplitFn, (_originalValue, newValue, position) => {
        const currentValue = value || '';
        const newPillText = `{${newValue}}`;
        const before = currentValue.substring(0, position.from);
        const after = currentValue.substring(position.to);
        const updatedValue = before + newPillText + after;
        onChange(updatedValue);
    }, () => closePanel(), (pillValue, _clickedPosition) => {
        const view = editorViewRef.current;
        if (view && view.state) {
            const currentText = view.state.doc.toString();
            const pillText = `{${pillValue}}`;
            let pillIndex = currentText.indexOf(pillText);
            if (pillIndex !== -1) {
                const cursorPos = pillIndex + pillText.length;
                view.dispatch({
                    selection: { anchor: cursorPos, head: cursorPos }
                });
                view.focus();
            }
        }
    }, expressionMatcher, disabled, readOnly, showFormatters), [allFields, pillConfiguration, pillValuesSplitFn, expressionMatcher, value, onChange, disabled, readOnly, showFormatters]);
    const extensions = React.useMemo(() => {
        const { keymap, EditorView, defaultKeymap, historyKeymap } = getCM();
        return [
            keymap.of([
                {
                    key: "Backspace",
                    run: (view) => {
                        const { state } = view;
                        const { main } = state.selection;
                        try {
                            const decorations = state.field(pillExtensionData.pillField);
                            let pillToDelete = null;
                            decorations.between(0, state.doc.length, (from, to, decoration) => {
                                if (decoration.spec.widget instanceof PillWidget) {
                                    if ((main.head === to || (main.head > from && main.head <= to)) && main.empty) {
                                        pillToDelete = { from, to };
                                        return false;
                                    }
                                }
                            });
                            if (pillToDelete) {
                                view.dispatch({
                                    changes: { from: pillToDelete.from, to: pillToDelete.to, insert: "" },
                                    selection: { anchor: pillToDelete.from }
                                });
                                return true;
                            }
                        }
                        catch (error) {
                            // Silently handle errors
                        }
                        return false;
                    }
                }
            ]),
            pillExtensionData.extension,
            keymap.of(defaultKeymap),
            keymap.of(historyKeymap),
            EditorView.theme({
                '.cm-editor': {
                    '&.cm-focused': {
                        outline: 'none'
                    }
                },
                '.cm-content': {
                    padding: '6px 8px',
                    minHeight: '32px',
                    lineHeight: '1.3'
                },
                '.uxpcore_pillinput__pill-container': {
                    display: 'inline-block',
                    verticalAlign: 'baseline'
                }
            })
        ];
    }, [pillExtensionData]);
    const renderSection = (section, index) => {
        var _a;
        if (!((_a = section === null || section === void 0 ? void 0 : section.fields) === null || _a === void 0 ? void 0 : _a.length))
            return null;
        return (_jsxs("div", { className: "uxpcore_pillinput__values-section", children: [_jsx("div", { className: "uxpcore_pillinput__values-section-header", children: section.label }), _jsx("div", { className: "uxpcore_pillinput__values", children: section.fields.map((field, i) => (_jsx("div", { className: "uxpcore_pillinput__values-item", children: _jsx(Pill, { value: field.value || '', pillConfiguration: pillConfiguration, onClick: () => {
                                var _a;
                                const finalVal = (_a = ([field.value, ...(field === null || field === void 0 ? void 0 : field.formatters) || []])) === null || _a === void 0 ? void 0 : _a.join('|');
                                insertAtCursor(`{${finalVal}} `);
                            }, pillValuesSplitFn: pillValuesSplitFn, allFields: allFields, expressionMatcher: expressionMatcher }) }, i))) })] }, index));
    };
    const renderOptionsPanel = () => (_jsx("div", { className: "uxpcore_pillinput__panel", children: _jsx("div", { className: "uxpcore_pillinput__sections", children: contextDataSections.map((section, index) => renderSection(section, index)) }) }));
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = ((value === null || value === void 0 ? void 0 : value.length) > 0 && !disabled && !readOnly) ? (_jsx("button", { type: "button", className: "uxpcore_input__clear-btn", onMouseDown: (e) => { e.preventDefault(); onClear ? onClear() : onChange(''); }, tabIndex: -1, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    const CodeMirrorComp = (_a = getCM()) === null || _a === void 0 ? void 0 : _a.ReactCodeMirror;
    return (_jsxs("div", { className: classNames("uxpcore_pillinput", className, {
            'uxpcore_input--full-width': fullWidth
        }), style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), title: inspect ? (value || '') : undefined, children: [_jsxs("div", { ref: inputContainerRef, className: classNames("uxpcore_input", "uxpcore_input--pillinput", stateClass, {
                    "uxpcore_input--has-content": (value === null || value === void 0 ? void 0 : value.length) > 0,
                    "uxpcore_input--has-suffix": !!clearButton,
                    "uxpcore_input--focus": isFocused || isPanelOpen,
                    "uxpcore_input--full-width": fullWidth
                }), children: [_jsx("div", { className: "uxpcore_pillinput__editor", children: CodeMirrorComp && (_jsx(CodeMirrorComp, { value: value || '', placeholder: placeholder, extensions: extensions, onChange: onChange, onFocus: handleFocus, onBlur: handleBlur, onCreateEditor: (view) => {
                                editorViewRef.current = view;
                            }, basicSetup: {
                                lineNumbers: false,
                                foldGutter: false,
                                dropCursor: true,
                                allowMultipleSelections: false,
                                indentOnInput: false,
                                bracketMatching: false,
                                closeBrackets: false,
                                autocompletion: false,
                                highlightSelectionMatches: false,
                                searchKeymap: false,
                                defaultKeymap: false
                            } })) }), clearButton && (_jsx("div", { className: "uxpcore_input__suffix", children: clearButton }))] }), _jsx(Dropdown, { externalTriggerRef: inputContainerRef, content: renderOptionsPanel(), position: "bottom-left", isOpen: isPanelOpen, onToggle: closePanel, minWidth: 250, maxWidth: 250 })] }));
});
export default PillInput;
